// Coherence lens. Looks at a whole room at once, not a single pick, and
// flags when too many uncoordinated things compete. This is the
// bachelor-pad / swap-meet detector, and it is the differentiator: you can
// spot "three fighting wood tones and four metals" from real finish data,
// but never from a generated fantasy render.
//
// Built on rules the spec already names, promoted to room scope:
//   M-01  metal consistency   two is a choice, three is an accident
//   WD-01 wood tone limit     max 2-3 tones, must share temperature
//   P-01  busyness budget     let one thing lead; quiet the rest
//
// Coherence findings are TASTE (kind "coherence"), so they are roastable.
// The safety gate in tone.ts governs the separate, never-joked categories
// (safety, durability, budget).

import type { Tone } from "./types.js";
import { effectiveTone } from "./tone.js";
import { ruleParam } from "./designRules.js";

// Thresholds from the shared rule data (design-laws.yaml mirror), so the
// coherence lens and the graph rule engine agree on what "too many" means.
const MAX_METALS = ruleParam("M-01", "max_visible_finishes", 2);
const MAX_WOOD_TONES = ruleParam("WD-01", "max_tones_per_sightline", 3);
const MAX_WOOD_SPREAD = ruleParam("WD-01", "max_temperature_spread", 0.5);
const MAX_LOUD = ruleParam("P-01", "max_loud_elements", 1);

export type MetalFamily = "warm" | "cool" | "neutral";

// One finish in the room. Only the fields relevant to a given item need be
// set: a faucet carries a metalFamily, a floor a woodTone, a rug a chroma.
export interface FinishItem {
  role: string; // "floor", "hardware", "faucet", "cabinet", "rug", ...
  label: string;
  metalFamily?: MetalFamily;
  woodTone?: { label: string; temperature: number };
  chroma?: number; // visual busyness 0..1
  areaWeight?: number; // visual dominance 0..1 (default 0.3)
}

export type CoherenceRule = "M-01" | "WD-01" | "P-01";
export type Severity = "notable" | "strong";

export interface CoherenceFinding {
  rule: CoherenceRule;
  severity: Severity;
  items: string[]; // the labels involved
  detail: string; // one-line neutral fact (count, temperature spread, ...)
}

export interface CoherenceReport {
  findings: CoherenceFinding[];
  metalFamilies: MetalFamily[];
  woodTones: string[];
  loudItems: string[];
  loadScore: number; // total visual busyness load
  verdict: "clean" | "busy" | "chaotic";
}

const LOUD = 0.4; // chroma at/above this reads as a loud, attention-grabbing element

export function coherence(items: FinishItem[]): CoherenceReport {
  const findings: CoherenceFinding[] = [];

  // M-01: distinct visible metal families.
  const metalItems = items.filter((i) => i.metalFamily);
  const metalFamilies = [...new Set(metalItems.map((i) => i.metalFamily!))];
  if (metalFamilies.length > MAX_METALS) {
    findings.push({
      rule: "M-01",
      severity: metalFamilies.length >= 4 ? "strong" : "notable",
      items: metalItems.map((i) => i.label),
      detail: `${metalFamilies.length} metal families visible (${metalFamilies.join(", ")})`,
    });
  }

  // WD-01: distinct wood tones, and whether they share temperature.
  const woodItems = items.filter((i) => i.woodTone);
  const woodTones = [...new Set(woodItems.map((i) => i.woodTone!.label))];
  const temps = woodItems.map((i) => i.woodTone!.temperature);
  const tempSpread = temps.length ? Math.max(...temps) - Math.min(...temps) : 0;
  if (woodTones.length > MAX_WOOD_TONES || (woodTones.length >= 2 && tempSpread > MAX_WOOD_SPREAD)) {
    findings.push({
      rule: "WD-01",
      severity: woodTones.length > MAX_WOOD_TONES ? "strong" : "notable",
      items: woodItems.map((i) => i.woodTone!.label),
      detail:
        woodTones.length > MAX_WOOD_TONES
          ? `${woodTones.length} wood tones in one sightline`
          : `wood tones span warm and cool (spread ${tempSpread.toFixed(2)})`,
    });
  }

  // P-01: competing focal points and total visual load.
  const loudItemsArr = items.filter((i) => (i.chroma ?? 0) >= LOUD);
  const loadScore = +items
    .reduce((s, i) => s + (i.chroma ?? 0) * (i.areaWeight ?? 0.3), 0)
    .toFixed(2);
  if (loudItemsArr.length > MAX_LOUD) {
    findings.push({
      rule: "P-01",
      severity: loudItemsArr.length >= 3 ? "strong" : "notable",
      items: loudItemsArr.map((i) => i.label),
      detail: `${loudItemsArr.length} loud elements competing for the eye`,
    });
  }

  const hasStrong = findings.some((f) => f.severity === "strong");
  const verdict: CoherenceReport["verdict"] =
    findings.length === 0 ? "clean" : hasStrong || findings.length >= 3 ? "chaotic" : "busy";

  return {
    findings,
    metalFamilies,
    woodTones,
    loudItems: loudItemsArr.map((i) => i.label),
    loadScore,
    verdict,
  };
}

// ---- Voicing ---------------------------------------------------------------
// Coherence findings carry structured data; here they become prose in the
// chosen tone. Routed through effectiveTone so the guardrail is honored in
// one place (coherence is roastable, but the plumbing is identical for any
// finding, which keeps the gate central).

function fixFor(rule: CoherenceRule): string {
  return rule === "M-01"
    ? "Pick two metal families and let the rest go."
    : rule === "WD-01"
    ? "Match the wood temperatures, or drop one tone."
    : "Let one element lead and quiet the others.";
}

function professional(f: CoherenceFinding): string {
  const list = f.items.join(", ");
  if (f.rule === "M-01") return `${f.detail}: ${list}. Two is a choice, three reads accidental. ${fixFor(f.rule)}`;
  if (f.rule === "WD-01") return `${f.detail}: ${list}. Keep to two or three tones that share a temperature. ${fixFor(f.rule)}`;
  return `${f.detail}: ${list}. ${fixFor(f.rule)}`;
}

function coach(f: CoherenceFinding): string {
  if (f.rule === "M-01") return `You're mixing a few metals (${f.items.join(", ")}). Two usually looks intentional; a third starts to feel accidental. ${fixFor(f.rule)}`;
  if (f.rule === "WD-01") return `Your wood tones aren't quite in sync yet (${f.items.join(", ")}). ${fixFor(f.rule)}`;
  return `A couple of pieces are competing (${f.items.join(", ")}). ${fixFor(f.rule)}`;
}

function roast(f: CoherenceFinding): string {
  const strong = f.severity === "strong";
  if (f.rule === "M-01") {
    return strong
      ? `${f.items.join(", ")}. This isn't mixed metals, it's a hardware yard sale. ${fixFor(f.rule)}`
      : `Three metals walk into a room. Two is a look, three is an accident. ${fixFor(f.rule)}`;
  }
  if (f.rule === "WD-01") {
    return strong
      ? `${f.items.join(", ")}: your floor and your furniture are not on speaking terms. ${fixFor(f.rule)}`
      : `Those wood tones are side-eyeing each other. ${fixFor(f.rule)}`;
  }
  return strong
    ? `${f.items.join(", ")} are all yelling at once. Pick a main character. ${fixFor(f.rule)}`
    : `Two things are fighting for the spotlight (${f.items.join(", ")}). ${fixFor(f.rule)}`;
}

export function renderCoherence(f: CoherenceFinding, tone: Tone): string {
  // Coherence is taste-adjacent, so the gate lets any voice through; using
  // effectiveTone keeps the guardrail honored uniformly.
  switch (effectiveTone(tone, "coherence")) {
    case "playful": return roast(f);
    case "coach": return coach(f);
    case "professional":
    default: return professional(f);
  }
}

export const VERDICT_COPY: Record<CoherenceReport["verdict"], string> = {
  clean: "Reads as one coordinated room.",
  busy: "A little busy. One or two things to reconcile.",
  chaotic: "Too many uncoordinated elements competing.",
};
